/**
 * JournalModule - Journal page (local-first).
 *
 * Two-mode view:
 * - "Write" mode: today's (or the open) entry in the TipTap editor.
 * - "Browse" mode: month-grouped grid of past entries with real content previews.
 *
 * Persistence is local-first via the entry store (Dexie/IndexedDB). Opening an
 * existing entry loads its saved content; it never blanks or duplicates it.
 *
 * @see Requirements 1.1, 2.1, 2.2
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEntryStore } from '@/stores/entryStore';
import { EntryEditor } from '@/components/editor/EntryEditor';
import { FloatingActionButton } from '@/components/ui/FloatingActionButton';
import { format } from 'date-fns';

export default function JournalModule() {
  const entries = useEntryStore((s) => s.entries);
  const currentEntry = useEntryStore((s) => s.currentEntry);
  const isLoading = useEntryStore((s) => s.isLoading);
  const createEntry = useEntryStore((s) => s.createEntry);
  const openEntry = useEntryStore((s) => s.openEntry);
  const updateEntry = useEntryStore((s) => s.updateEntry);
  const deleteEntry = useEntryStore((s) => s.deleteEntry);
  const loadEntries = useEntryStore((s) => s.loadEntries);

  const [viewMode, setViewMode] = useState<'today' | 'browse'>('today');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const todayDisplay = useMemo(
    () => format(new Date(), 'EEEE, MMMM d, yyyy'),
    []
  );

  // Load all entries on mount (hydrate from IndexedDB).
  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  // When entering Write mode with nothing open, open/create today's entry.
  useEffect(() => {
    if (viewMode === 'today' && !currentEntry) {
      void openEntry(today);
    }
  }, [viewMode, today, currentEntry, openEntry]);

  // Entries for the selected month, newest first.
  const monthEntries = useMemo(() => {
    if (!entries) return [];
    return entries
      .filter((e) => e.entryDate.startsWith(selectedMonth))
      .sort((a, b) => b.entryDate.localeCompare(a.entryDate));
  }, [entries, selectedMonth]);

  // Months that actually have entries (for the selector).
  const availableMonths = useMemo(() => {
    if (!entries || entries.length === 0) return [];
    const months = new Set(entries.map((e) => e.entryDate.substring(0, 7)));
    return Array.from(months).sort().reverse();
  }, [entries]);

  const handleContentChange = useCallback(
    (json: string) => {
      if (currentEntry) {
        void updateEntry(currentEntry.entryDate, json);
      }
    },
    [currentEntry, updateEntry]
  );

  const handleCreateToday = useCallback(() => {
    void createEntry(today); // idempotent: opens today's entry if it exists
    setViewMode('today');
  }, [createEntry, today]);

  const handleOpenEntry = useCallback(
    (entryDate: string) => {
      void openEntry(entryDate); // loads existing content, no duplication
      setViewMode('today');
    },
    [openEntry]
  );

  const handleDeleteEntry = useCallback(
    (e: React.MouseEvent, entryDate: string) => {
      e.stopPropagation();
      if (window.confirm(`Delete the entry for ${entryDate}? This cannot be undone.`)) {
        void deleteEntry(entryDate);
      }
    },
    [deleteEntry]
  );

  const saved = currentEntry?.syncStatus === 'synced';

  return (
    <div className="flex flex-col h-full p-6 gap-6">
      {/* Header with view toggle */}
      <div className="flex items-center justify-between">
        <h1 className="text-heading-1 font-extrabold text-white tracking-tight">Journal</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('today')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              viewMode === 'today'
                ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-lg'
                : 'bg-white/5 text-text-secondary hover:bg-white/10'
            }`}
          >
            ✍️ Write
          </button>
          <button
            onClick={() => setViewMode('browse')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              viewMode === 'browse'
                ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-lg'
                : 'bg-white/5 text-text-secondary hover:bg-white/10'
            }`}
          >
            📖 Browse
          </button>
        </div>
      </div>

      {/* TODAY / WRITE VIEW */}
      {viewMode === 'today' && (
        <div className="flex-1 min-h-0 flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <p className="text-text-secondary text-body">
              {currentEntry
                ? format(
                    new Date(currentEntry.entryDate + 'T00:00:00'),
                    'EEEE, MMMM d, yyyy'
                  )
                : todayDisplay}
            </p>
            {currentEntry && (
              <span className="text-xs text-text-tertiary px-3 py-1 rounded-full glass">
                {saved ? '✓ Saved' : '● Saving…'}
              </span>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : currentEntry ? (
              <div className="card-gradient h-full">
                <EntryEditor
                  content={currentEntry.content}
                  onChange={handleContentChange}
                  editable={true}
                />
              </div>
            ) : (
              <div className="card-gradient flex flex-col items-center justify-center h-full text-center gap-4">
                <span className="text-5xl" aria-hidden="true">✍️</span>
                <h2 className="text-heading-3 font-bold text-white">Start Today's Entry</h2>
                <p className="text-text-secondary">
                  Capture your thoughts, ideas, and reflections.
                </p>
                <button
                  onClick={handleCreateToday}
                  className="btn-primary text-white px-8 py-3 text-body font-semibold"
                >
                  Begin Writing
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* BROWSE VIEW */}
      {viewMode === 'browse' && (
        <div className="flex-1 min-h-0 flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-4 py-2 rounded-full text-sm bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-amber-400/50"
              aria-label="Select month"
            >
              {availableMonths.map((month) => {
                const [y, m] = month.split('-');
                const label = format(new Date(Number(y), Number(m) - 1, 1), 'MMMM yyyy');
                return (
                  <option key={month} value={month}>
                    {label}
                  </option>
                );
              })}
              {availableMonths.length === 0 && (
                <option value={selectedMonth}>No entries yet</option>
              )}
            </select>
            <span className="text-text-tertiary text-sm">
              {monthEntries.length} {monthEntries.length === 1 ? 'entry' : 'entries'}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto">
            {monthEntries.length === 0 ? (
              <div className="card-gradient flex flex-col items-center justify-center py-16 text-center gap-3">
                <span className="text-4xl" aria-hidden="true">📝</span>
                <p className="text-text-secondary">No entries this month</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {monthEntries.map((entry) => {
                  const dateObj = new Date(entry.entryDate + 'T00:00:00');
                  const dayLabel = format(dateObj, 'EEEE, MMM d');
                  const hasText = entry.preview && entry.preview.length > 0;

                  return (
                    <div
                      key={entry.entryDate}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenEntry(entry.entryDate)}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter' || ev.key === ' ') {
                          ev.preventDefault();
                          handleOpenEntry(entry.entryDate);
                        }
                      }}
                      className="card-gradient text-left p-5 hover:scale-[1.02] transition-all cursor-pointer group relative"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-amber-400 mb-2">{dayLabel}</p>
                        <button
                          type="button"
                          onClick={(ev) => handleDeleteEntry(ev, entry.entryDate)}
                          aria-label={`Delete entry for ${dayLabel}`}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-text-tertiary hover:text-red-400 text-xs px-2 py-1 rounded"
                        >
                          🗑
                        </button>
                      </div>
                      <p className="text-text-secondary text-sm line-clamp-3">
                        {hasText ? entry.preview : <span className="italic opacity-60">Empty entry</span>}
                      </p>
                      <p className="text-xs text-text-tertiary mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        Click to open →
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {viewMode === 'browse' && (
        <FloatingActionButton onClick={handleCreateToday} label="+ Today's Entry" gradient="amber" />
      )}
    </div>
  );
}
